import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RelationshipTypeForm from './RelationshipTypeForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ActiveStatusPill, SystemPill } from '../../../shared/Pill';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { ChevronRight } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { askConfirm } from '../../../shared/AppDialogs';

const rowInputStyle = { padding: '5px 8px', borderRadius: 5, border: '1px solid #bfc8d6', fontSize: 13 };
const SYSTEM_RENAMABLE_TITLE = "System Use — click Edit to rename; required by Optim's core functionality.";
const SYSTEM_STAGE_TITLE = "System Use — can't be renamed, reordered or deactivated; required by Optim's core functionality.";
// Active is what a converted Prospect lands on and what makes a Customer
// quotable — see relationshipStageController.js.
const ALWAYS_REQUIRES_TERMS = ['active'];

// One Relationship Type's own Stage pipeline (Lead -> Qualified -> ...),
// expanded inline under its row rather than a separate page/route — small
// enough a list not to need its own screen, and keeping it inline means
// there's one place ("Relationship Types") to manage the whole model from.
// Prospect's and Customer's built-in Stages are System (relationshipStage.js):
// locked against rename, reorder and deactivate, since features are built
// on them. The tenant adds Custom Stages around them. Only Prospect Stages
// show the Open column — open Stages are the Pipeline board's columns.
function StagePanel({ relationshipType, showError, dismissError, onChanged }) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', sort_order: 100, description: '' });
  const [addDraft, setAddDraft] = useState({ name: '', sort_order: 100, is_open: true, description: '' });
  const showOpen = relationshipType.system_key === 'prospect';
  const [saving, setSaving] = useState(false);

  const fetchStages = () => {
    setLoading(true);
    axios.get('/api/relationship-stages', { params: { relationship_type_id: relationshipType.id, status: 'all' } })
      .then((res) => setStages(res.data.data || []))
      .catch(() => setStages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStages();
    // eslint-disable-next-line
  }, [relationshipType.id]);

  const startEdit = (stage) => {
    setEditingId(stage.id);
    setEditDraft({ name: stage.name, sort_order: stage.sort_order, description: stage.description || '' });
  };

  const saveEdit = async (stage) => {
    setSaving(true);
    try {
      await axios.put(`/api/relationship-stages/${stage.id}`, editDraft);
      setEditingId(null);
      fetchStages();
      onChanged();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  // Saved on flip, like every other toggle on a settings row — no separate
  // Save step for a single boolean.
  const setStageFlag = async (stage, field, value) => {
    try {
      await axios.put(`/api/relationship-stages/${stage.id}`, { name: stage.name, sort_order: stage.sort_order, [field]: value });
      fetchStages();
      onChanged();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
  };

  const toggleActive = async (stage) => {
    try {
      await axios.patch(`/api/relationship-stages/${stage.id}/${stage.is_active ? 'deactivate' : 'reactivate'}`);
      fetchStages();
      onChanged();
      dismissError();
    } catch (err) {
      showError(err, `${stage.is_active ? 'Deactivate' : 'Reactivate'} failed`);
    }
  };

  const addStage = async () => {
    if (!addDraft.name.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/relationship-stages', { relationship_type_id: relationshipType.id, ...addDraft, is_open: showOpen ? addDraft.is_open : true });
      setAddDraft({ name: '', sort_order: 100, is_open: true, description: '' });
      fetchStages();
      onChanged();
      dismissError();
    } catch (err) {
      showError(err, 'Add failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '4px 16px 16px 48px', background: '#f7f9fc' }}>
      {loading ? (
        <div style={{ fontSize: 13, color: '#888' }}>Loading Stages...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, maxWidth: 980 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600 }}>Stage</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600 }} title="What the Stage means, shown under its Pipeline column">Description</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }}>Sort</th>
              {showOpen && <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 70 }} title="Records at an open Stage show on the Pipeline board">Open</th>}
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 170 }} title="Entering this Stage needs a Tier and a Customer Price Group, and Entities here can be quoted">Requires Commercial Terms</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }}>Status</th>
              <th style={{ width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s) => (
              <tr key={s.id} style={{ borderTop: '1px solid #e3e7ee' }}>
                {editingId === s.id ? (
                  <>
                    <td style={{ padding: '4px 6px' }}>
                      {s.is_system_default ? s.name : <input className="styled-input" style={rowInputStyle} value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} />}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input className="styled-input" style={rowInputStyle} maxLength={255} value={editDraft.description} onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))} autoFocus={s.is_system_default} />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      {s.is_system_default ? s.sort_order : <input className="styled-input" type="number" style={{ ...rowInputStyle, width: 60 }} value={editDraft.sort_order} onChange={(e) => setEditDraft((d) => ({ ...d, sort_order: e.target.value }))} />}
                    </td>
                    {showOpen && <td style={{ padding: '4px 6px', textAlign: 'center' }}>{s.is_open ? 'Yes' : 'No'}</td>}
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>{s.requires_commercial_terms ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}><ActiveStatusPill active={s.is_active} /></td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <SaveButton type="button" loading={saving} onClick={() => saveEdit(s)} style={{ padding: '2px 10px', fontSize: 12 }} />
                      <CancelButton onClick={() => setEditingId(null)} style={{ padding: '2px 10px', fontSize: 12, marginLeft: 6 }} />
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '4px 6px' }}>{s.name}</td>
                    <td style={{ padding: '4px 6px', color: s.description ? undefined : '#9aa5b5' }}>{s.description || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{s.sort_order}</td>
                    {showOpen && (
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex' }}>
                          <ToggleSwitch
                            checked={!!s.is_open}
                            disabled={s.is_system_default}
                            title={s.is_system_default ? `Fixed for ${s.name}` : 'Show records at this Stage on the Pipeline board'}
                            onChange={(value) => setStageFlag(s, 'is_open', value)}
                          />
                        </div>
                      </td>
                    )}
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex' }}>
                        <ToggleSwitch
                          checked={!!s.requires_commercial_terms}
                          disabled={ALWAYS_REQUIRES_TERMS.includes(s.system_key)}
                          title={ALWAYS_REQUIRES_TERMS.includes(s.system_key) ? `Always on for ${s.name}` : 'Require a Tier and a Customer Price Group to enter this Stage'}
                          onChange={(value) => setStageFlag(s, 'requires_commercial_terms', value)}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>{s.is_system_default ? <SystemPill title={SYSTEM_STAGE_TITLE} /> : <ActiveStatusPill active={s.is_active} />}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                      {/* A System Stage's Edit changes only its Description. */}
                      <RowActionsMenu
                        items={s.is_system_default ? [
                          { key: 'edit', label: 'Edit Description', onClick: () => startEdit(s) },
                        ] : [
                          { key: 'edit', label: 'Edit', onClick: () => startEdit(s) },
                          s.is_active
                            ? { key: 'deactivate', label: 'Deactivate', danger: true, onClick: () => toggleActive(s) }
                            : { key: 'reactivate', label: 'Reactivate', onClick: () => toggleActive(s) },
                        ]}
                      />
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #e3e7ee' }}>
              <td style={{ padding: '6px 6px' }}>
                <input className="styled-input" style={rowInputStyle} placeholder="New Stage name" value={addDraft.name} onChange={(e) => setAddDraft((d) => ({ ...d, name: e.target.value }))} />
              </td>
              <td style={{ padding: '6px 6px' }}>
                <input className="styled-input" style={rowInputStyle} maxLength={255} placeholder="What it means" value={addDraft.description} onChange={(e) => setAddDraft((d) => ({ ...d, description: e.target.value }))} />
              </td>
              <td style={{ padding: '6px 6px' }}>
                <input className="styled-input" type="number" style={{ ...rowInputStyle, width: 60 }} value={addDraft.sort_order} onChange={(e) => setAddDraft((d) => ({ ...d, sort_order: e.target.value }))} />
              </td>
              {showOpen && (
                <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex' }}>
                    <ToggleSwitch
                      checked={addDraft.is_open}
                      title="Show records at this Stage on the Pipeline board"
                      onChange={(value) => setAddDraft((d) => ({ ...d, is_open: value }))}
                    />
                  </div>
                </td>
              )}
              <td></td>
              <td></td>
              <td style={{ padding: '6px 6px', textAlign: 'right' }}>
                <button type="button" disabled={saving || !addDraft.name.trim()} onClick={addStage} style={{ border: 'none', background: 'none', color: '#1976d2', cursor: addDraft.name.trim() ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: addDraft.name.trim() ? 1 : 0.5 }}>
                  + Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function RelationshipTypeTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/relationship-types', { params: { status } });
      setRows(res.data.data || []);
      setPagination({ page: 1, limit: res.data.data?.length || 0, total: res.data.data?.length || 0, pages: 1 });
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Relationship Type "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/relationship-types/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/relationship-types/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete Relationship Type "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/relationship-types/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/relationship-types/${editItem.id}`, data);
      } else {
        await axios.post('/api/relationship-types', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Relationship Types</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Relationship Type
            </button>
          )}
        </div>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        What a Commercial Entity IS to the business (Customer, Supplier, Manufacturer...). Click a row to manage its Stage pipeline.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <RelationshipTypeForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '35%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Stages</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => {
              // Contact is the fallback Type and never has Stages, so its row
              // doesn't open a Stage panel.
              const hasStagePanel = item.system_key !== 'contact';
              return (
              <React.Fragment key={item.id}>
                <tr
                  className="list-row"
                  onClick={hasStagePanel ? () => setExpandedId((cur) => (cur === item.id ? null : item.id)) : undefined}
                  style={{ cursor: hasStagePanel ? 'pointer' : 'default' }}
                  title={hasStagePanel ? 'Click to manage Stages' : undefined}
                >
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    {hasStagePanel && <ChevronRight size={16} strokeWidth={2} style={{ color: '#9aa5b5', transform: expandedId === item.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>
                    {item.name}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 13, color: '#5a6b85' }}>{item.stages?.length || 0} stage{item.stages?.length === 1 ? '' : 's'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                    {item.usage_count ? (
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                        title={`See which entities hold the "${item.name}" Relationship Type`}
                        onClick={() => navigate(`/market-intel-all-entities?relationship_type_id=${item.id}&relationship_type_name=${encodeURIComponent(item.name)}`)}
                      >
                        {item.usage_count}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    {item.system_key ? <SystemPill title={SYSTEM_RENAMABLE_TITLE} /> : <ActiveStatusPill active={item.is_active} />}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                        ...(item.is_active
                          ? [{ key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm || !!item.system_key, disabledTitle: item.system_key ? "A built-in Relationship Type can't be deactivated" : (showForm ? 'Finish editing first' : undefined), onClick: () => handleDeactivate(item) }]
                          : [
                              { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                              { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
                            ]),
                      ]}
                    />
                  </td>
                </tr>
                {hasStagePanel && expandedId === item.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <StagePanel relationshipType={item} showError={showError} dismissError={dismissError} onChanged={fetchRows} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Relationship Types"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default RelationshipTypeTable;
